import {
  Children,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';

import { cn } from '@utils/cn';

import type { SliderProps, SliderRef } from './Slider.types';

import {
  getSliderRootStyle,
  sliderItemClasses,
  sliderRootClasses,
  sliderTrackClasses
} from './Slider.styles';

export const Slider = forwardRef<SliderRef, SliderProps>(
  ({ children, desktopVisible = 3, mobileVisible = 1, className, onIndexChange }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const currentIndex = activeIndex;
    const totalItems = useMemo(() => Children.toArray(children), [children]).length;

    const rootStyle = getSliderRootStyle(desktopVisible, mobileVisible);

    const getVisibleCount = useCallback(
      () => (window.innerWidth < 992 ? mobileVisible : desktopVisible),
      [mobileVisible, desktopVisible]
    );

    const getMaxIndex = useCallback(
      () => Math.max(0, totalItems - getVisibleCount()),
      [totalItems, getVisibleCount]
    );

    const scrollDOMToIndex = useCallback(
      (index: number) => {
        const el = containerRef.current;
        if (el) {
          const visible = getVisibleCount();
          const gapPx = 8;
          const itemWidth = (el.clientWidth + gapPx) / visible;
          el.scrollTo({ left: itemWidth * index, behavior: 'smooth' });
        }
      },
      [getVisibleCount]
    );

    const scrollToIndex = useCallback(
      (index: number) => {
        if (onIndexChange) {
          onIndexChange(index);
        } else {
          setActiveIndex(index);
        }
        scrollDOMToIndex(index);
        return index;
      },
      [onIndexChange, scrollDOMToIndex]
    );

    // useImperativeHandle customizes the instance value that is exposed to parent components when using ref.
    useImperativeHandle(
      ref,
      () => ({
        prev: () => scrollToIndex(Math.max(0, currentIndex - 1)),
        next: () => scrollToIndex(Math.min(getMaxIndex(), currentIndex + 1)),
        scrollTo: scrollToIndex,
        getCurrentIndex: () => currentIndex,
        getMaxIndex
      }),
      [currentIndex, scrollToIndex, getMaxIndex]
    );

    return (
      <div ref={containerRef} className={cn(sliderRootClasses, className)} style={rootStyle}>
        <div data-slider-track className={sliderTrackClasses()}>
          {Children.toArray(children).map((child, index) => (
            <div key={index} className={sliderItemClasses}>
              {child}
            </div>
          ))}
        </div>
      </div>
    );
  }
);

Slider.displayName = 'Slider';
